import React, { useEffect, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaPaperPlane,
  FaSpinner,
  FaVolumeUp,
} from "react-icons/fa";
import HorenAudioPlayer from "../exam/HorenAudioPlayer";

const API = process.env.REACT_APP_API_BASE_URL;

type Module = "LESEN" | "HOREN" | "SCHREIBEN";

interface Question {
  id: number;
  module: Module;
  questionNumber: number;
  questionType: string;
  questionText: string;
  instructions: string | null;
  contextText: string | null;
  contextImageUrl: string | null;
  contextAudioUrl: string | null;
  options: string | null;
  blankCount: number | null;
  points: number;
  autoGradable: boolean;
}

interface StartResponse {
  attemptId: number;
  remainingSeconds: number;
  totalDurationMinutes: number;
  classTestTitle: string;
  level: string;
  chapterRange: string;
  lesenDuration: number;
  horenDuration: number;
  schreibenDuration: number;
  horenAudioUrl: string | null;
  questions: Question[];
}

interface Props {
  assignmentId: number;
  onBack: () => void;
  onSubmitted: () => void;
}

const MODULES: Module[] = ["LESEN", "HOREN", "SCHREIBEN"];

const MODULE_LABEL: Record<Module, string> = {
  LESEN: "Lesen",
  HOREN: "Hören",
  SCHREIBEN: "Schreiben",
};

const parseOptions = (raw: string | null): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === "object") return Object.values(parsed);
  } catch {
    // plain comma-separated fallback
  }
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
};

const fmtTime = (secs: number) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const StudentTakeClassTest: React.FC<Props> = ({ assignmentId, onBack, onSubmitted }) => {
  const [data, setData] = useState<StartResponse | null>(null);
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Map<number, string>>(new Map());
  const [currentModule, setCurrentModule] = useState<Module>("LESEN");
  const [remainingSecs, setRemainingSecs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const start = async () => {
      try {
        const res = await fetch(
          `${API}/api/student/class-tests/assignments/${assignmentId}/start`,
          { method: "POST", credentials: "include" }
        );
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          alert(d.message || "Could not start test.");
          onBack();
          return;
        }
        const d: StartResponse = await res.json();
        setData(d);
        setAttemptId(d.attemptId);
        setRemainingSecs(d.remainingSeconds ?? d.totalDurationMinutes * 60);
      } catch {
        alert("Network error. Please try again.");
        onBack();
      } finally {
        setLoading(false);
      }
    };
    start();
  }, [assignmentId]);

  // Timer
  useEffect(() => {
    if (!data || submitted) return;
    timerRef.current = setInterval(() => {
      setRemainingSecs((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current!);
          handleSubmit(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current!);
  }, [data, submitted]);

  const saveAnswer = async (questionId: number, answer: string) => {
    if (!attemptId) return;
    setSavingId(questionId);
    try {
      await fetch(`${API}/api/student/class-tests/attempts/${attemptId}/answer`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ questionId, answer }),
      });
    } catch (e) {
      console.error("save answer failed", e);
    } finally {
      setSavingId(null);
    }
  };

  const handleAnswer = (questionId: number, value: string) => {
    setAnswers((prev) => new Map(prev).set(questionId, value));
    saveAnswer(questionId, value);
  };

  const handleSubmit = async (auto = false) => {
    if (!attemptId) return;
    if (!auto && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    setShowConfirm(false);
    setSubmitting(true);
    try {
      await fetch(`${API}/api/student/class-tests/attempts/${attemptId}/submit`, {
        method: "POST",
        credentials: "include",
      });
      setSubmitted(true);
      clearInterval(timerRef.current!);
      onSubmitted();
    } catch {
      alert("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <FaSpinner className="animate-spin text-4xl text-[#009fe3]" />
      </div>
    );
  }

  if (!data) return null;

  const moduleQuestions = data.questions.filter((q) => q.module === currentModule);
  const currentIdx = MODULES.indexOf(currentModule);
  const isLastModule = currentIdx === MODULES.length - 1;

  const timerColor =
    remainingSecs < 120 ? "text-red-600" : remainingSecs < 300 ? "text-yellow-600" : "text-gray-700";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 text-sm sm:text-base truncate">
              {data.classTestTitle}
            </div>
            <div className="text-xs text-gray-500">
              {data.level} · {data.chapterRange?.replace(/_/g, " ")}
            </div>
          </div>

          {/* Timer */}
          <div className={`flex items-center gap-1.5 font-mono font-bold text-lg ${timerColor}`}>
            <FaClock size={14} />
            {fmtTime(remainingSecs)}
          </div>

          <button
            onClick={() => handleSubmit()}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? <FaSpinner className="animate-spin" size={13} /> : <FaPaperPlane size={13} />}
            Submit Test
          </button>
        </div>

        {/* Module tabs */}
        <div className="max-w-4xl mx-auto px-4 flex border-t border-gray-100">
          {MODULES.map((mod) => {
            const qs = data.questions.filter((q) => q.module === mod);
            const answered = qs.filter((q) => answers.has(q.id)).length;
            return (
              <button
                key={mod}
                onClick={() => setCurrentModule(mod)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  currentModule === mod
                    ? "border-[#009fe3] text-[#009fe3]"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                {MODULE_LABEL[mod]}
                <span className="ml-1.5 text-xs">
                  ({answered}/{qs.length})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Questions */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {/* Hören audio player — shown once at top of Hören section */}
        {currentModule === "HOREN" && data.horenAudioUrl && (
          <HorenAudioPlayer audioUrl={data.horenAudioUrl} />
        )}

        {moduleQuestions.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            No questions for this module.
          </div>
        ) : (
          moduleQuestions
            .sort((a, b) => a.questionNumber - b.questionNumber)
            .map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                answer={answers.get(q.id) ?? ""}
                onAnswer={(val) => handleAnswer(q.id, val)}
                saving={savingId === q.id}
              />
            ))
        )}

        {/* Module navigation */}
        <div className="flex justify-between pt-4">
          {currentIdx > 0 ? (
            <button
              onClick={() => setCurrentModule(MODULES[currentIdx - 1])}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FaArrowLeft size={12} /> {MODULE_LABEL[MODULES[currentIdx - 1]]}
            </button>
          ) : (
            <div />
          )}
          {!isLastModule ? (
            <button
              onClick={() => setCurrentModule(MODULES[currentIdx + 1])}
              className="flex items-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg text-sm hover:bg-[#007cb5] transition-colors"
            >
              {MODULE_LABEL[MODULES[currentIdx + 1]]} <FaArrowRight size={12} />
            </button>
          ) : (
            <button
              onClick={() => handleSubmit()}
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? <FaSpinner className="animate-spin" size={12} /> : <FaPaperPlane size={12} />}
              Submit Test
            </button>
          )}
        </div>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-semibold text-gray-900 text-lg mb-2">Submit Test?</h3>
            <p className="text-sm text-gray-600 mb-1">
              {MODULES.map((mod) => {
                const qs = data.questions.filter((q) => q.module === mod);
                const ans = qs.filter((q) => answers.has(q.id)).length;
                return (
                  <span key={mod} className="block">
                    {MODULE_LABEL[mod]}: {ans}/{qs.length} answered
                  </span>
                );
              })}
            </p>
            <p className="text-sm text-gray-500 mt-2 mb-4">
              You cannot change answers after submission.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleSubmit(true)}
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? <FaSpinner className="animate-spin" size={12} /> : <FaCheckCircle size={12} />}
                Yes, Submit
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Play-once audio player ───────────────────────────────────────────────────

const PlayOnceAudio: React.FC<{ src: string }> = ({ src }) => {
  const ref = useRef<HTMLAudioElement>(null);
  const [played, setPlayed] = useState(false);
  const [playing, setPlaying] = useState(false);

  const play = () => {
    if (played || !ref.current) return;
    ref.current.play();
    setPlaying(true);
  };

  return (
    <div className="flex items-center gap-3 mb-3 bg-purple-50 border border-purple-200 rounded-lg px-4 py-2.5">
      <audio
        ref={ref}
        src={src}
        onEnded={() => { setPlayed(true); setPlaying(false); }}
      />
      <button
        onClick={play}
        disabled={played}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          played
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-purple-600 text-white hover:bg-purple-700"
        }`}
      >
        {playing ? <FaSpinner className="animate-spin" size={13} /> : <FaVolumeUp size={13} />}
        {played ? "Played" : playing ? "Playing…" : "Play Audio"}
      </button>
      {!played && (
        <span className="text-xs text-purple-600">Listen carefully — plays once only</span>
      )}
      {played && (
        <span className="text-xs text-gray-400">Audio has been played</span>
      )}
    </div>
  );
};

// ─── Question Card ─────────────────────────────────────────────────────────────

interface QCardProps {
  question: Question;
  answer: string;
  onAnswer: (val: string) => void;
  saving: boolean;
}

const QuestionCard: React.FC<QCardProps> = ({ question: q, answer, onAnswer, saving }) => {
  const options = parseOptions(q.options);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs text-gray-400 mb-1">
            Question {q.questionNumber} · {q.points} point{q.points !== 1 ? "s" : ""}
          </div>
          {q.instructions && (
            <div className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 mb-2 border border-blue-100">
              {q.instructions}
            </div>
          )}
          {q.contextText && (
            <div className="text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 mb-3 whitespace-pre-wrap">
              {q.contextText}
            </div>
          )}
          {q.contextImageUrl && (
            <img
              src={q.contextImageUrl}
              alt="Context"
              className="max-w-xs rounded-lg border border-gray-200 mb-3"
            />
          )}
          {q.contextAudioUrl && (
            <PlayOnceAudio src={q.contextAudioUrl} />
          )}
          {q.questionType !== "FILL_BLANKS" && (
            <div className="font-medium text-gray-900">{q.questionText}</div>
          )}
        </div>
        {saving && <FaSpinner className="animate-spin text-[#009fe3] shrink-0 mt-1" size={14} />}
      </div>

      {/* Answer input */}
      {(() => {
        const qBlankCount = q.blankCount ?? 1;
        const isMulti = qBlankCount > 1;

        const getMultiAnswers = (): string[] => {
          try {
            const p = JSON.parse(answer || "[]");
            if (Array.isArray(p)) return p;
          } catch {}
          return answer ? [answer] : [];
        };
        const updateMulti = (idx: number, val: string) => {
          const arr = [...getMultiAnswers()];
          while (arr.length <= idx) arr.push("");
          arr[idx] = val;
          onAnswer(JSON.stringify(arr));
        };

        if ((q.questionType === "MULTIPLE_CHOICE" || q.questionType === "MULTIPLE_CHOICE_4") && options.length > 0) {
          if (isMulti) {
            const multiAnswers = getMultiAnswers();
            return (
              <div className="space-y-4">
                {Array.from({ length: qBlankCount }).map((_, qi) => (
                  <div key={qi} className="space-y-1.5">
                    <p className="text-xs font-semibold text-gray-500">Question {q.questionNumber + qi}</p>
                    <div className="space-y-1.5">
                      {options.map((opt, i) => {
                        const letter = String.fromCharCode(97 + i);
                        return (
                          <label
                            key={i}
                            className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                              multiAnswers[qi] === letter
                                ? "border-[#009fe3] bg-blue-50"
                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`q-${q.id}-${qi}`}
                              value={letter}
                              checked={multiAnswers[qi] === letter}
                              onChange={() => updateMulti(qi, letter)}
                              className="text-[#009fe3]"
                            />
                            <span className="font-mono text-xs text-gray-400 w-5">{letter})</span>
                            <span className="text-gray-800">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          return (
            <div className="space-y-2">
              {options.map((opt, i) => {
                const letter = String.fromCharCode(97 + i);
                return (
                  <label
                    key={i}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      answer === letter
                        ? "border-[#009fe3] bg-blue-50"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`q-${q.id}`}
                      value={letter}
                      checked={answer === letter}
                      onChange={() => onAnswer(letter)}
                      className="text-[#009fe3]"
                    />
                    <span className="font-mono text-xs text-gray-400 w-5">{letter})</span>
                    <span className="text-sm text-gray-800">{opt}</span>
                  </label>
                );
              })}
            </div>
          );
        }

        if (q.questionType === "TRUE_FALSE") {
          if (isMulti) {
            const multiAnswers = getMultiAnswers();
            return (
              <div className="space-y-3">
                {Array.from({ length: qBlankCount }).map((_, qi) => (
                  <div key={qi} className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-500 w-24 shrink-0">Question {q.questionNumber + qi}</span>
                    <div className="flex gap-2">
                      {["Richtig", "Falsch"].map((opt) => (
                        <label
                          key={opt}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm ${
                            multiAnswers[qi] === opt
                              ? "border-[#009fe3] bg-blue-50 text-[#009fe3] font-medium"
                              : "border-gray-200 text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`q-${q.id}-${qi}`}
                            value={opt}
                            checked={multiAnswers[qi] === opt}
                            onChange={() => updateMulti(qi, opt)}
                            className="text-[#009fe3]"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          return (
            <div className="flex gap-3">
              {["Richtig", "Falsch"].map((opt) => (
                <label
                  key={opt}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                    answer === opt
                      ? "border-[#009fe3] bg-blue-50 text-[#009fe3] font-medium"
                      : "border-gray-200 text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name={`q-${q.id}`}
                    value={opt}
                    checked={answer === opt}
                    onChange={() => onAnswer(opt)}
                    className="text-[#009fe3]"
                  />
                  {opt}
                </label>
              ))}
            </div>
          );
        }

        return null;
      })()}

      {q.questionType === "FILL_BLANKS" && (() => {
        const parts = q.questionText?.split("___") || [""];
        const blankCount = q.blankCount ?? Math.max(1, parts.length - 1);
        const getAnswers = (): string[] => {
          try {
            const p = JSON.parse(answer || "[]");
            if (Array.isArray(p)) return p;
          } catch {}
          return answer ? [answer] : [];
        };
        const currentAnswers = getAnswers();
        const updateBlank = (idx: number, val: string) => {
          const arr = [...currentAnswers];
          while (arr.length <= idx) arr.push("");
          arr[idx] = val;
          onAnswer(JSON.stringify(arr));
        };
        // If question text has ___ markers, render inline; otherwise stack inputs
        const hasInlineMarkers = parts.length - 1 >= blankCount && blankCount > 1 && parts.length > 1;
        if (hasInlineMarkers) {
          return (
            <div className="text-base text-gray-900 leading-relaxed">
              {parts.map((part, i) => (
                <React.Fragment key={i}>
                  <span>{part}</span>
                  {i < parts.length - 1 && (
                    <input
                      type="text"
                      value={currentAnswers[i] || ""}
                      onChange={(e) => updateBlank(i, e.target.value)}
                      className="inline-block mx-1 px-2 py-0.5 border-b-2 border-[#009fe3] bg-blue-50 rounded-sm text-center w-28 focus:outline-none focus:border-[#0077aa]"
                      placeholder={`(${i + 1})`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          );
        }
        // Stacked inputs (photo-only or single blank)
        return (
          <div className="space-y-2">
            {Array.from({ length: blankCount }).map((_, idx) => (
              <div key={idx} className={blankCount > 1 ? "flex items-center gap-2" : ""}>
                {blankCount > 1 && (
                  <span className="text-xs text-gray-500 shrink-0 w-20">Q{q.questionNumber + idx}</span>
                )}
                <input
                  type="text"
                  value={currentAnswers[idx] || ""}
                  onChange={(e) => updateBlank(idx, e.target.value)}
                  className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  placeholder={blankCount > 1 ? `Answer for Q${q.questionNumber + idx}…` : "Fill in the blank…"}
                />
              </div>
            ))}
          </div>
        );
      })()}

      {(q.questionType === "TEXT_INPUT" ||
        q.questionType === "ESSAY" ||
        q.questionType === "EMAIL" ||
        q.questionType === "LETTER") && (
        <textarea
          rows={q.questionType === "TEXT_INPUT" ? 2 : 8}
          value={answer}
          onChange={(e) => onAnswer(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent resize-none"
          placeholder={
            q.questionType === "TEXT_INPUT"
              ? "Your answer…"
              : "Write your answer here…"
          }
        />
      )}

      {q.questionType === "MATCHING" && (
        <textarea
          rows={4}
          value={answer}
          onChange={(e) => onAnswer(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] resize-none"
          placeholder="e.g. 1-A, 2-C, 3-B…"
        />
      )}
    </div>
  );
};

export default StudentTakeClassTest;
